import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { WinAnimator2 } from "../../CustomComponents/WinAnimator2";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { StageController } from "../../Components/StageController";
import { DataManager } from "../../Managers/DataManager";

type IWin = Communication.Game.IWin;

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _outroAnimDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;

    private _symbols: Symbols;
    private _machine: Machine;
    private _noWinContainer: ContainerElement;
    private _winAnimator: WinAnimator2;

    private _backgroundWin: SwitchElement;
    private _rays: SwitchElement;
    private _logo: ImageElement;

    private shockWave1: PIXI.filters.ShockwaveFilter;
    private shockWave2: PIXI.filters.ShockwaveFilter;
    private shockWave3: PIXI.filters.ShockwaveFilter;
    private shockWave4: PIXI.filters.ShockwaveFilter;
    private shockWave5: PIXI.filters.ShockwaveFilter;

    private sceneWidth: number;
    private sceneHeight: number;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._outroAnimDelay = CreateFrameTimer("outroAnimDelay");
        this._outroAnimDelay.on(FrameTimerEvents.Complete, this.outroAnimFinished.bind(this));

        StageController.baseStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (node.hasAttribute("Width")) {
            this.sceneWidth = parseInt(node.getAttribute("Width"));
        }

        if (node.hasAttribute("Height")) {
            this.sceneHeight = parseInt(node.getAttribute("Height"));
        }

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "BackgroundWin" && this.childs[key] instanceof SwitchElement) {
                    this._backgroundWin = this.childs[key];
                }

                if (key === "Rays" && this.childs[key] instanceof SwitchElement) {
                    this._rays = this.childs[key];
                }

                if (key === "Logo" && this.childs[key] instanceof ImageElement) {
                    this._logo = this.childs[key];
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        this.shocks();
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(this._symbols, this._paylines, this._noWinContainer);
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }
        this.toggleElements(false);

        if (ModuleManager.gambleActive()) {
            this.allSwitchElementToCase("INIT_INTRO");
            <SwitchElement>this.childs["ReelsSeparator"].goToCase("STOP");
        }
    }

    disableLogo(): void {
        if (this._logo != null) {
            this.removeChild(this._logo);
            this._logo = null;
        }
    }

    toggleElements(visible: boolean, long: boolean = false): void {
        if (long) {
            if (visible) {
                if (this._noWinContainer != null) {
                    Tweens.removeTweens(this._noWinContainer, "all");
                    Tweens.alphaTween(this._noWinContainer, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                    this._noWinContainer.visible = true;
                }
                if (this._paylines != null) {
                    Tweens.removeTweens(this._paylines, "all");
                    Tweens.alphaTween(this._paylines, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                    this._paylines.visible = true;
                }
                if (this._winAnimator != null) {
                    Tweens.removeTweens(this._winAnimator, "all");
                    Tweens.alphaTween(this._winAnimator, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                    this._winAnimator.visible = true;
                }
            } else {
                if (this._noWinContainer != null) {
                    Tweens.removeTweens(this._noWinContainer, "all");
                    Tweens.alphaTween(this._noWinContainer, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (this._paylines != null) {
                    Tweens.removeTweens(this._paylines, "all");
                    Tweens.alphaTween(this._paylines, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (this._winAnimator != null) {
                    Tweens.removeTweens(this._winAnimator, "all");
                    Tweens.alphaTween(this._winAnimator, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
            }
        } else {
            if (this._noWinContainer != null) {
                this._noWinContainer.alpha = 1;
                this._noWinContainer.visible = visible;
            }
            if (this._paylines != null) {
                this._paylines.alpha = 1;
                this._paylines.visible = visible;
            }
            if (this._winAnimator != null) {
                this._winAnimator.alpha = 1;
                this._winAnimator.visible = visible;
            }
        }
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        this.allSwitchElementToCase("INIT_INTRO");

        if (GameManager.baseResponse.totalWin > 0) {
            if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "NORMAL") {
                this._backgroundWin.goToCase("WIN");
            }

            PlayList.loop("SND_BaseRecapLoop");

            if (this._rays != null && this._rays.getCurrentCase() === "NORMAL") {
                this._rays.goToCase("WIN");
            }
        } else {
            PlayList.loop("SND_BaseLoop");
        }
        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
        this.toggleElements(true);
    }

    collect(): void {
        if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "WIN") {
            this._backgroundWin.goToCase("WIN_TO_NORMAL");
        }
        if (this._rays != null && this._rays.getCurrentCase() === "WIN") {
            this._rays.goToCase("WIN_TO_NORMAL");
        }

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");
        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecapLoop");

        if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "WIN") {
            this._backgroundWin.goToCase("WIN_TO_NORMAL");
        }
        if (this._rays != null && this._rays.getCurrentCase() === "WIN") {
            this._rays.goToCase("WIN_TO_NORMAL");
        }
        PlayList.loop("SND_BaseLoop");
        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        if (this._machine.actualReelStopped == 0) {
            this.stopShockWaves();
        }

        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(): void {
        if (GameManager.baseResponse.totalWin > 0) {
            if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "NORMAL") {
                this._backgroundWin.goToCase("WIN");
            }
            if (this._rays != null && this._rays.getCurrentCase() === "NORMAL") {
                this._rays.goToCase("WIN");
            }
        }
        this._wasFinished = false;
        this._controller.machineStopped();
        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        StageController.baseStage = true;
        if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "WIN") {
            this._backgroundWin.goToCase("WIN_TO_NORMAL");
        }
        if (this._rays != null && this._rays.getCurrentCase() === "WIN") {
            this._rays.goToCase("WIN_TO_NORMAL");
        }

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.loop("SND_BaseRecapLoop", 15);
            PlayList.stop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop", 15);
        }

        if (long) {
            this._introDelay.start(0);

            this.allSwitchElementToCase("INTRO");
            <SwitchElement>this.childs["SceneTransition"].goToCase("INTRO");
        } else {
            this.allSwitchElementToCase("GAMBLE_TRANSITION");
            <SwitchElement>this.childs["ReelsSeparator"].goToCase("START");

            this._introDelay.start(15);
        }
    }

    introFinished(): void {
        this.toggleElements(true, true);

        if (this.childs["Logo"]) {
            Tweens.customTween(this.childs["Logo"], {
                tween: "alpha",
                tweenValues: [0, 1],
                tweenDuration: [15],
                tweenEase: ["linear"],
            });
        }

        StageController.baseStage = true;
        StageController.freespinStage = false;
        this._controller.introFinished();
    }

    outro(): void {
        if (this._backgroundWin != null && this._backgroundWin.getCurrentCase() === "WIN") {
            this._backgroundWin.goToCase("WIN_TO_NORMAL");
        }
        if (this._rays != null && this._rays.getCurrentCase() === "WIN") {
            this._rays.goToCase("WIN_TO_NORMAL");
        }

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        <SwitchElement>this.childs["ReelsSeparator"].goToCase("OUTRO");

        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");

        if (ModuleManager.freespinsActive()) {
            PlayList.play("SND_FSIntro");

            Tweens.customTween(this.childs["Paylines"], {
                tween: "alpha",
                tweenValues: [1, 0],
                tweenDuration: [30],
                tweenEase: ["linear"],
            });

            Tweens.customTween(this.childs["WinAnimator"], {
                tween: "alpha",
                tweenValues: [1, 0],
                tweenDuration: [30],
                tweenEase: ["linear"],
            });

            if (this.childs["Logo"]) {
                Tweens.customTween(this.childs["Logo"], {
                    tween: "alpha",
                    tweenValues: [1, 0],
                    tweenDuration: [30],
                    tweenEase: ["linear"],
                });
            }

            <SwitchElement>this.childs["SceneTransition"].goToCase("OUTRO");

            this._outroDelay.start(50);
        } else {
            if (wasFinished) {
                this.outroAnimFinished();
            } else {
                if (inHighWins) {
                    this._outroAnimDelay.start(60);
                } else {
                    this._outroAnimDelay.start(30);
                }
            }
        }
    }

    outroAnimFinished(): void {
        <SwitchElement>this.childs["SceneTransition"].goToCase("GAMBLE_TRANSITION");
        this.toggleElements(false, true);

        PlayList.play("SND_Transition");

        // this._outroDelay.start(5);
        this.outroFinished();
    }

    outroFinished(): void {
        this.stopShockWaves();

        this._outroDelay.stop();
        if (ModuleManager.freespinsActive()) {
            this.toggleElements(false, true);
            this.allSwitchElementToCase("EMPTY");
        }
        this._controller.outroFinished();
    }

    selectPayline(): void {}

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    public shocks(): void {
        let options: ShockwaveOptions = new ShockwaveOptions();
        options.wavelength = this.sceneWidth * 0.2;
        options.amplitude = this.sceneHeight * 0.06;
        options.radius = this.sceneHeight * 0.7;
        options.brightness = 1;

        this.shockWave1 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave1.enabled = false;

        this.shockWave2 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave2.enabled = false;

        this.shockWave3 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave3.enabled = false;

        this.shockWave4 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave4.enabled = false;

        this.shockWave5 = new PIXI.filters.ShockwaveFilter([this.sceneWidth * 0.5, this.sceneHeight * 0.5], options, 3);
        this.shockWave5.enabled = false;

        this.filters = [this.shockWave1, this.shockWave2, this.shockWave3, this.shockWave4, this.shockWave5];
    }

    public shock1(x: number, y: number, time: number = 160): void {
        this.shockWave1.center = [x, y];
        this.shockWave1.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave1.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave1.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    public shock2(x: number, y: number, time: number = 160): void {
        this.shockWave2.center = [x, y];
        this.shockWave2.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave2.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave2.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    public shock3(x: number, y: number, time: number = 160): void {
        this.shockWave3.center = [x, y];
        this.shockWave3.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave3.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave3.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    public shock4(x: number, y: number, time: number = 160): void {
        this.shockWave4.center = [x, y];
        this.shockWave4.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave4.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave4.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    public shock5(x: number, y: number, time: number = 160): void {
        this.shockWave5.center = [x, y];
        this.shockWave5.enabled = true;

        Tweens.customTween(this, {
            tween: "shockWave5.time",
            tweenValues: [0, 1.5 * (this.sceneWidth / 1920)],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
            callbackFunction: (): void => {
                this.shockWave1.enabled = false;
            },
        });

        Tweens.customTween(this, {
            tween: "shockWave5.amplitude",
            tweenValues: [this.sceneHeight * 0.06, 0],
            tweenDuration: [time],
            tweenEase: ["linear"],
            tweenLoop: false,
        });
    }

    private stopShockWaves(): void {
        this.shockWave1.enabled = false;
        this.shockWave2.enabled = false;
        this.shockWave3.enabled = false;
        this.shockWave4.enabled = false;
        this.shockWave5.enabled = false;
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }
}

class ShockwaveOptions {
    amplitude?: number;
    wavelength?: number;
    brightness?: number;
    speed?: number;
    radius?: number;
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/